import React, { useEffect, useMemo, useState } from "react";
import { BiEdit, BiTrash } from "react-icons/bi";
import { IoGrid } from "react-icons/io5";
import { FaTasks, FaCalendarAlt, FaUser, FaFileInvoice, FaCheckCircle, FaClock, FaRedo } from "react-icons/fa";
import { MdDescription } from "react-icons/md";
import { FaTableList } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import draftToHtml from "draftjs-to-html";

import { getUserDataByID } from "../../services/api/AuthApi";
import { updateUnseenNotification } from "../../services/api/NotificationsApi";
import { useBubbleSettings } from "../../context/BubbleSettingsContext";
import { formatText } from "../TruncateParties";
import DeleteConfirmationModal from "../DeleteConfirmationModal/DeleteConfirmationModal";
import "./style/RenderTasks.scss";

export const RenderTasks = ({
  tasks = [],
  setModalTab,
  setShowModal,
  onEditTask,
  onDeleteTask,
}) => {
  const navigate = useNavigate();
  const [userMap, setUserMap] = useState({});
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const { bubbleSettings, fetchAndUpdateBubbleSettings } = useBubbleSettings();
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    taskId: null,
    taskName: "",
  });
  const [viewMode, setViewMode] = useState("table");

  // --- Helpers ---
  const truncateText = (text, wordLimit = 20, charLimit = 100) => {
    if (!text) return "";
    const words = text.split(/\s+/);
    if (words.length <= wordLimit && text.length <= charLimit) return text;
    let truncated = words.slice(0, wordLimit).join(" ");
    if (truncated.length > charLimit) {
      truncated = truncated.slice(0, charLimit).replace(/\s+\S*$/, "");
    }
    return truncated + "...";
  };

  const getWordCount = (text) => (text ? text.split(/\s+/).filter((w) => w.length > 0).length : 0);
  const getCharCount = (text) => (text ? text.length : 0);

  const getPlainTextFromHtml = (html) => {
    if (!html) return "";
    const div = document.createElement("div");
    div.innerHTML = html;
    return (div.textContent || div.innerText || "").trim();
  };

  const toggleDescription = (taskId, e) => {
    e.stopPropagation();
    setExpandedDescriptions((prev) => ({ ...prev, [taskId]: !prev[taskId] }));
  };

  // --- Effects ---
  useEffect(() => {
    const assignedToIds = tasks.flatMap((task) => task.assignedTo || []);
    const assignedByIds = tasks.map((task) => task.assignedBy).filter(Boolean);
    const allIds = [...assignedToIds, ...assignedByIds];
    const uniqueIds = [...new Set(allIds.map((id) => String(id).trim()))];

    if (uniqueIds.length > 0) {
      Promise.all(uniqueIds.map((id) => getUserDataByID(id)))
        .then((responses) => {
          const map = {};
          responses.forEach((res) => {
            const user = res?.data;
            if (user && user._id) {
              map[String(user._id).trim()] = user;
            }
          });
          setUserMap(map);
        })
        .catch((err) => console.error("Error fetching users:", err));
    }
  }, [tasks]);

  const { newOrUpdatedTasks, taskNotificationMap } = useMemo(() => {
    const entries = [
      ...(bubbleSettings?.case?.highCourt || []),
      ...(bubbleSettings?.case?.districtCourt || []),
      ...(bubbleSettings?.case?.supremeCourt || []),
    ];
    const newOrUpdatedTasks = {};
    const taskNotificationMap = {};

    entries.forEach((entry) => {
      entry.task?.forEach((taskEntry) => {
        if (["Task Created", "Task Updated"].includes(taskEntry.action)) {
          const taskId = taskEntry.source;
          newOrUpdatedTasks[taskId] = taskEntry.action;
          taskNotificationMap[taskId] = taskEntry.id;
        }
      });
    });
    return { newOrUpdatedTasks, taskNotificationMap };
  }, [bubbleSettings]);

  // --- Handlers ---
  const handleDeleteClick = (e, taskId, taskName) => {
    e.stopPropagation();
    setDeleteModal({ isOpen: true, taskId, taskName });
  };

  const handleConfirmDelete = async () => {
    await onDeleteTask(deleteModal.taskId);
    setDeleteModal({ isOpen: false, taskId: null, taskName: "" });
  };

  const handleCancelDelete = () => {
    setDeleteModal({ isOpen: false, taskId: null, taskName: "" });
  };

  const handleTaskClick = async (taskId) => {
    const notificationId = taskNotificationMap[taskId];
    if (notificationId) {
      try {
        await updateUnseenNotification(notificationId);
        await fetchAndUpdateBubbleSettings();
      } catch (err) {
        console.error("Failed to update notification:", err);
      }
    }
    navigate(`/home/tasks/viewtask/${taskId}`);
  };

  const handleEditClick = (task) => {
    setModalTab("Tasks");
    setShowModal(true);
    onEditTask(task);
  };

  // --- Renderers ---

  const renderTableView = () => (
    <div className="table-responsive">
      <table className="documents-table">
        <thead>
          <tr>
            <th className="td-th-casedet">Task Name</th>
            <th className="td-th-casedet">Due Date</th>
            <th className="td-th-casedet">Court Ref</th>
            <th className="td-th-casedet">Assigned By</th>
            <th className="td-th-casedet">Assigned To</th>
            <th style={{ textAlign: "center" }} className="td-th-casedet">Actions</th>
          </tr>
        </thead>
        <tbody>
          {tasks.map((task, index) => {
            const actionType = newOrUpdatedTasks[task._id];
            return (
              <tr
                key={index}
                onClick={() => handleTaskClick(task._id)}
                style={{ cursor: "pointer" }}
                title="Click to view details"
              >
                <td className="td-th-casedet">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {task.taskName}
                    {actionType && (
                      <span className={`task-badge-inline ${actionType === "Task Created" ? "new" : "updated"}`}>
                        {actionType === "Task Created" ? "New" : "Updated"}
                      </span>
                    )}
                  </div>
                </td>
                <td className="td-th-casedet">{new Date(task.dueDate).toLocaleDateString()}</td>
                <td className="td-th-casedet">{task.courtRef}</td>
                <td className="td-th-casedet">
                  {task.assignedBy ? formatText(userMap[task.assignedBy]?.name || "Unknown") : "Not Specified"}
                </td>
                <td className="td-th-casedet">
                  {Array.isArray(task.assignedTo) && task.assignedTo.length > 0
                    ? task.assignedTo.map((id) => userMap[id]?.name || "Unknown").join(", ")
                    : "Not Assigned"}
                </td>
                
                <td className="td-th-casedet">
                  <div className="editdel-table-actions">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditClick(task);
                      }}
                      className="editbtn"
                      title="Edit"
                    >
                      <BiEdit size={16} />
                    </button>
                    <button
                      onClick={(e) => handleDeleteClick(e, task._id, task.taskName)}
                      className="delbtn"
                      title="Delete"
                    >
                      <BiTrash size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const renderGridView = () => (
    <div className="tasks-grid-layout">
      {tasks.map((task, index) => {
        const actionType = newOrUpdatedTasks[task._id];

        let descriptionContent = null;
        let hasDescription = false;

        if (task.description) {
           let html = "";
           try {
             const raw = typeof task.description === "string" ? JSON.parse(task.description) : task.description;
             html = (raw && raw.blocks) ? draftToHtml(raw) : task.description;
           } catch (e) {
             html = task.description;
           }

           let fixedHtml = html
             .replace(/<a ([^>]*href=["'])(?!https?:\/\/)([^"'>]+)(["'][^>]*)>/gi, "<a $1https://$2$3>")
             .replace(/<a /gi, '<a target="_blank" rel="noopener noreferrer" ');

           const plainText = getPlainTextFromHtml(fixedHtml);
           
           if (plainText.length > 0) {
              hasDescription = true;
              const isExpanded = expandedDescriptions[task._id];
              
              if ((getWordCount(plainText) > 20 || getCharCount(plainText) > 120) && !isExpanded) {
                 descriptionContent = (
                   <>
                     <span>{truncateText(plainText, 20, 120)}...</span>
                     <button className="read-more-btn" onClick={(e) => toggleDescription(task._id, e)}>
                       Read More
                     </button>
                   </>
                 );
              } else {
                 descriptionContent = (
                   <>
                     <span dangerouslySetInnerHTML={{ __html: fixedHtml }} />
                     {(getWordCount(plainText) > 20 || getCharCount(plainText) > 120) && (
                       <button className="read-more-btn" onClick={(e) => toggleDescription(task._id, e)}>
                         Show Less
                       </button>
                     )}
                   </>
                 );
              }
           }
        }

        return (
          <div
            key={index}
            className="modern-task-card"
            onClick={() => handleTaskClick(task._id)}
            style={{ cursor: "pointer", position: "relative" }} 
          >
            {/* GRID BADGE: Absolute Ribbon */}
            {actionType && (
              <div className={`task-badge ${actionType === "Task Created" ? "new" : "updated"}`}>
                {actionType === "Task Created" ? "New" : "Updated"}
              </div>
            )}

            <div className="card-header-strip">
              <div className="header-left">
                <div className="task-title-badge">
                  <FaTasks className="type-icon" />
                  <span className="title-text">{task.taskName}</span>
                </div>
              </div>

              <div className="header-actions">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditClick(task);
                  }}
                  className="action-btn edit-btn"
                  title="Edit"
                >
                  <BiEdit size={16} />
                </button>
                <button
                  onClick={(e) => handleDeleteClick(e, task._id, task.taskName)}
                  className="action-btn delete-btn"
                  title="Delete"
                >
                  <BiTrash size={16} />
                </button>
              </div>
            </div>

            <div className="card-content-body">
              {task.recurringTask && (
                <div className="recurring-info">
                  <FaRedo className="rec-icon" /> 
                  Recurring {task.recurringTask.times} times a {task.recurringTask.in}
                </div>
              )}

              {/* First Row - Due Date and Court Ref in same row */}
              <div className="detail-row-horizontal">
                <div className="detail-row detail-row-half">
                  <div className="detail-label">
                    <FaCalendarAlt className="row-icon" /> Due Date
                  </div>
                  <div className="detail-value">{new Date(task.dueDate).toLocaleDateString()}</div>
                </div>
                <div className="detail-row detail-row-half">
                  <div className="detail-label">
                    <FaFileInvoice className="row-icon" /> Court Ref
                  </div>
                  <div className="detail-value">{task.courtRef}</div>
                </div>
              </div>

              {/* Second Row - Status and Assigned To in same row */}
              <div className="detail-row-horizontal">
                <div className="detail-row detail-row-half">
                  <div className="detail-label">
                    <FaClock className="row-icon" /> Status
                  </div>
                  <div className="detail-value">
                    <span className={`completion-badge ${task.isCompleted ? 'completed' : 'pending'}`}>
                      {task.isCompleted ? <><FaCheckCircle/> Completed</> : "Pending"}
                    </span>
                  </div>
                </div>
                <div className="detail-row detail-row-half">
                  <div className="detail-label">
                    <FaUser className="row-icon" /> Assigned To
                  </div>
                  <div className="detail-value">
                    {Array.isArray(task.assignedTo) && task.assignedTo.length > 0
                      ? task.assignedTo.map((id) => userMap[id]?.name || "Unknown").join(", ")
                      : "Not Assigned"}
                  </div>
                </div>
              </div>

              {/* Description Row */}
              {hasDescription && (
                <div className="detail-row">
                  <div className="detail-label">
                    <MdDescription className="row-icon" /> Description
                  </div>
                  <div className="detail-value description-text">
                    {descriptionContent}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <>
      <div className="tab-content">
        <div className="summary header-actions-container">
          <div className="sum-amount">
            <p className="margin-zero exp-count">
              <strong>Total Tasks - </strong> {tasks.length}
            </p>
          </div>
          <div className="actions-right">
            {tasks.length > 0 && (
              <div className="view-toggles">
                <button
                  className={`toggle-btn ${viewMode === "grid" ? "active" : ""}`}
                  onClick={() => setViewMode("grid")}
                  title="Grid View"
                >
                  <IoGrid />
                </button>
                <button
                  className={`toggle-btn ${viewMode === "table" ? "active" : ""}`}
                  onClick={() => setViewMode("table")}
                  title="Table View"
                >
                  <FaTableList />
                </button>
              </div>
            )}
            <div className="tab-add">
              <button
                onClick={() => {
                  setModalTab("Tasks");
                  setShowModal(true);
                  onEditTask(null);
                }}
              >
                Add Task
              </button>
            </div>
          </div>
        </div>

        {tasks.length === 0 ? (
          <div className="no-data-container">
            <h3 className="no-data-title">No Tasks Found</h3>
            <p className="no-data-message">
              No tasks have been created for this case yet. Click "Add Task" to create your first task.
            </p>
          </div>
        ) : viewMode === "table" ? (
          renderTableView()
        ) : (
          renderGridView()
        )}
      </div>
      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        itemType={`this task "${deleteModal.taskName}"`}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </>
  );
};